import React from "react";
import classNames from "classnames";
import Tag from "components/Tag";
import { SALES_EMAIL } from "constants/config";
import path from "constants/path";
import { SubscriptionPlan } from "enums/subscription-plans";
import { useRouter } from "next/router";
import { formatCurrency } from "helpers/format";
import { getAnnualCost, getMonthlyCost } from "helpers/pricing";
import { getAnnualSaving } from "helpers/products";
import useLabels from "hooks/useLabels";
import PricingFeatures from "../PricingFeatures";
import { usePricingData, useSubscriptionProducts } from "../../hooks";
import { SubscriptionPlanIcon } from "../../utils";
import { Button, ButtonProps, Heading, Text } from "design_system";

type PricingCardProps = {
  onClick: (price?: any, product?: any) => void;
  title?: string;
  plan: string;
  product?: any;
  price?: any;
  currentPlan?: SubscriptionPlan;
  isAnnualSelected: boolean;
  className?: string;
  isSignedIn?: boolean;
  buttonOverride?: {
    text?: string;
    variant?: ButtonProps["variant"];
    isDisabled?: boolean;
  };
};

function PricingCard({
  onClick,
  title,
  plan,
  product,
  price,
  currentPlan,
  isAnnualSelected,
  className,
  isSignedIn = false,
  buttonOverride,
}: PricingCardProps) {
  const [pricingLabels] = useLabels("screens.pricing");
  const router = useRouter();

  // Get pricing data from hooks instead of Redux
  const { pricingData } = usePricingData();
  const { products } = useSubscriptionProducts();

  const premium = pricingData?.premium;
  const free = pricingData?.free;
  const deep = pricingData?.deep;
  const enterprise = pricingData?.enterprise;
  const team = pricingData?.team;

  let buttonText = "";
  let isButtonDisabled = false;
  let buttonVariant: ButtonProps["variant"] = "secondary";
  let featuresList: string[] = [];
  let subtitle = "";

  switch (plan) {
    case SubscriptionPlan.FREE.toLowerCase():
      buttonText =
        isSignedIn && currentPlan === SubscriptionPlan.FREE
          ? pricingLabels["current-plan"]
          : pricingLabels["try-for-free"];
      buttonVariant = "tertiary";
      featuresList = free?.features ?? [];
      subtitle = pricingLabels["free-subtitle"];
      isButtonDisabled = isSignedIn && currentPlan === SubscriptionPlan.FREE;
      break;
    case SubscriptionPlan.ENTERPRISE.toLowerCase():
      buttonText =
        currentPlan === SubscriptionPlan.ENTERPRISE
          ? pricingLabels["current-plan"]
          : pricingLabels["contact-sales"];
      isButtonDisabled = currentPlan === SubscriptionPlan.ENTERPRISE;
      featuresList = enterprise?.features ?? [];
      subtitle = pricingLabels["enterprise-subtitle"];
      buttonVariant = "secondary";

      break;
    case SubscriptionPlan.TEAM.toLowerCase():
      buttonText =
        currentPlan === SubscriptionPlan.TEAM
          ? pricingLabels["manage-current-plan"]
          : pricingLabels["get-started"];
      isButtonDisabled = false;
      buttonVariant =
        currentPlan === SubscriptionPlan.TEAM ? "tertiary" : "primary";
      featuresList = team?.features ?? [];
      subtitle = pricingLabels["team-subtitle"];
      title = pricingLabels["team-title"];
      break;
    case SubscriptionPlan.DEEP.toLowerCase():
      featuresList = deep?.features ?? [];
      subtitle = pricingLabels["deep-subtitle"];

      // Deep button logic based on current plan
      if (currentPlan === SubscriptionPlan.DEEP) {
        buttonText = pricingLabels["manage-current-plan"];
        buttonVariant = "tertiary";
        isButtonDisabled = false;
      } else if (currentPlan === SubscriptionPlan.PREMIUM) {
        // Pro users see primary upgrade button
        buttonText = pricingLabels["upgrade-to-deep"] || "Upgrade to Deep";
        buttonVariant = "primary";
        isButtonDisabled = false;
      } else if (!isSignedIn || currentPlan === SubscriptionPlan.FREE) {
        // Free users and not signed in see secondary upgrade button
        buttonText = pricingLabels["upgrade-to-deep"] || "Upgrade to Deep";
        buttonVariant = "secondary";
        isButtonDisabled = false;
      }
      // Team/Enterprise handled by button hiding logic
      break;
    default:
      featuresList = premium?.features ?? [];
      subtitle = pricingLabels["premium-subtitle"];

      if (currentPlan === SubscriptionPlan.PREMIUM) {
        // Premium users see "Manage Current Plan"
        buttonText = pricingLabels["manage-current-plan"];
        buttonVariant = "tertiary";
        isButtonDisabled = false;
      } else {
        // Free users and non-signed in users see "Upgrade to Pro"
        // Note: Deep users won't see a button at all (handled by button visibility logic)
        buttonText = pricingLabels["upgrade-to-pro"];
        buttonVariant = "primary";
        isButtonDisabled = false;
      }
  }

  const monthlyCost = getMonthlyCost(price);
  const annualCost = getAnnualCost(price);

  const handleClickButton = () => {
    if (plan === SubscriptionPlan.ENTERPRISE.toLowerCase()) {
      window.location.href = "mailto:" + SALES_EMAIL;
      return;
    }

    // If it's the current plan for Premium, Deep, or Team, navigate to subscription page
    if (
      (currentPlan === SubscriptionPlan.PREMIUM &&
        plan === SubscriptionPlan.PREMIUM.toLowerCase()) ||
      (currentPlan === SubscriptionPlan.DEEP &&
        plan === SubscriptionPlan.DEEP.toLowerCase()) ||
      (currentPlan === SubscriptionPlan.TEAM &&
        plan === SubscriptionPlan.TEAM.toLowerCase())
    ) {
      router.push(path.SUBSCRIPTION);
      return;
    }

    onClick(price ? price : null, product);
  };

  return (
    <div data-testid="pricing-card" className={className}>
      <section className="sm:h-[124px]">
        <section className="flex items-center gap-3">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <SubscriptionPlanIcon plan={plan} size="lg" />
              <Heading as="h3" size="x-sm">
                {title}
              </Heading>
            </div>

            <Text as="h4">{subtitle}</Text>
          </div>
        </section>
      </section>

      <section className="flex items-center w-full mt-3">
        {price ? (
          <div className={classNames("w-full", isAnnualSelected && "sm:h-16")}>
            <div className="w-full">
              <Text
                as="span"
                family="mono"
                size="lg"
                weight="bold"
                data-testid="pricing-card-unit-amount"
              >
                {formatCurrency(monthlyCost / 100)}
                <Text
                  as="span"
                  size="x-sm"
                  weight="regular"
                  theme="muted"
                  className="ml-1"
                >
                  {
                    pricingLabels[
                      product?.metadata?.tier.toLowerCase() ==
                      SubscriptionPlan.TEAM.toLowerCase()
                        ? "per-month-per-seat"
                        : "per-month"
                    ]
                  }
                </Text>
              </Text>
            </div>
            {isAnnualSelected ? (
              <div className="mt-2 flex items-center justify-between">
                <Text as="span" size="x-sm" weight="regular" theme="muted">
                  {pricingLabels["billed-annually"].replace(
                    "{BILLED}",
                    formatCurrency(Math.ceil(annualCost / 100))
                  )}
                </Text>
                {price &&
                  (isAnnualSelected ||
                    (!isAnnualSelected &&
                      SubscriptionPlan.PREMIUM.toLowerCase() === plan)) && (
                    <div className="flex gap-2">
                      <Tag
                        label={pricingLabels[
                          product?.metadata?.tier.toLowerCase() ==
                          SubscriptionPlan.TEAM.toLowerCase()
                            ? "saving-team"
                            : "saving"
                        ].replace(
                          "{SAVINGS}",
                          formatCurrency(
                            Math.ceil(
                              getAnnualSaving(
                                plan,
                                products ?? [],
                                isAnnualSelected,
                                false
                              ) / 100
                            )
                          )
                        )}
                        variant="YES"
                        size="sm"
                      />
                    </div>
                  )}
              </div>
            ) : null}
          </div>
        ) : plan === SubscriptionPlan.FREE.toLowerCase() ? (
          <div className={classNames("w-full", isAnnualSelected && "sm:h-16")}>
            <Text
              as="span"
              family="mono"
              size="lg"
              weight="bold"
              data-testid="pricing-card-unit-amount"
            >
              $0
              <Text
                as="span"
                size="x-sm"
                weight="regular"
                theme="muted"
                className="ml-1"
              >
                {pricingLabels["per-month"]}
              </Text>
            </Text>
          </div>
        ) : plan === SubscriptionPlan.ENTERPRISE.toLowerCase() ? (
          <div className={classNames("w-full", isAnnualSelected && "sm:h-16")}>
            <Text
              as="span"
              size="lg"
              weight="bold"
              family="mono"
              className="uppercase"
            >
              {pricingLabels["custom"]}
            </Text>
          </div>
        ) : (
          <div className={isAnnualSelected ? "sm:h-16" : "sm:h-7"} />
        )}
      </section>

      <section className="w-full mt-6">
        {(plan === SubscriptionPlan.FREE.toLowerCase() &&
          isSignedIn &&
          currentPlan !== SubscriptionPlan.FREE) ||
        ((plan === SubscriptionPlan.PREMIUM.toLowerCase() ||
          plan === SubscriptionPlan.DEEP.toLowerCase()) &&
          isSignedIn &&
          (currentPlan === SubscriptionPlan.TEAM ||
            currentPlan === SubscriptionPlan.ENTERPRISE)) ||
        (plan === SubscriptionPlan.PREMIUM.toLowerCase() &&
          isSignedIn &&
          currentPlan === SubscriptionPlan.DEEP) ? (
          <div className="sm:h-10" />
        ) : (
          <Button
            variant={buttonOverride?.variant ?? buttonVariant}
            isDisabled={buttonOverride?.isDisabled ?? isButtonDisabled}
            data-intercom-target="filter button"
            onClick={handleClickButton}
            className="w-full"
          >
            {buttonOverride?.text ?? buttonText}
          </Button>
        )}
      </section>
      <div className="mt-12">
        <PricingFeatures featuresList={featuresList} />
      </div>
    </div>
  );
}

export default PricingCard;
